import React, { useState, useEffect, useRef } from 'react';

import { API, authHeaders, authHeadersOnly, UPLOADS_URL } from '../services/apiConfig';

const STATUS_COLORS = {
  OPEN:        { bg: '#fef3c7', color: '#d97706' },
  IN_PROGRESS: { bg: '#dbeafe', color: '#1d4ed8' },
  RESOLVED:    { bg: '#dcfce7', color: '#16a34a' },
  CLOSED:      { bg: '#f3f4f6', color: '#6b7280' },
};

export default function ContactQueries() {
  const [queries, setQueries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('ALL');
  const [expanded, setExpanded] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pdfMenuRef = useRef(null);

  useEffect(() => { fetchQueries(); }, []);

  useEffect(() => {
    const handler = (e) => { if (pdfMenuRef.current && !pdfMenuRef.current.contains(e.target)) setShowPdfMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchQueries = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contact`, { headers: authHeadersOnly() });
      const data = res.ok ? await res.json() : [];
      const sortedData = Array.isArray(data) ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];
      setQueries(sortedData);
    } catch { setQueries([]); }
    setLoading(false);
  };

  const downloadPdf = async (months) => {
    setShowPdfMenu(false);
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/contact/pdf?months=${months}`, { headers: authHeadersOnly() });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `queries-last-${months}months.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('PDF download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const updateStatus = async (id, status) => {
    await fetch(`${API}/api/contact/${id}/status?status=${status}`, { method: 'PUT', headers: authHeadersOnly() });
    const query = queries.find(q => q.id === id);
    if (query) {
      try {
        await fetch(`${API}/api/contact/notify-status-update`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ queryId: id, email: query.email, status, queryType: query.queryType, invoiceNo: query.invoiceNo })
        });
      } catch (err) { console.log('Notification sent'); }
    }
    fetchQueries();
  };

  const deleteQuery = async (id) => {
    if (!window.confirm('Delete this query?')) return;
    await fetch(`${API}/api/contact/${id}`, { method: 'DELETE', headers: authHeadersOnly() });
    fetchQueries();
  };

  const filtered = filter === 'ALL' ? queries : queries.filter(q => q.status === filter);
  const SHOW = expanded ? filtered : filtered.slice(0, 6);

  const counts = ['OPEN','IN_PROGRESS','RESOLVED','CLOSED'].reduce((a, s) => {
    a[s] = queries.filter(q => q.status === s).length;
    return a;
  }, {});

  const btnStyle = { background: '#232f3e', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' };

  return (
    <div style={{ background: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0', marginTop: '32px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1a1a1a' }}>📬 Contact Queries</h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#888' }}>Customer queries submitted via the Contact page</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={fetchQueries} style={btnStyle}>↻ Refresh</button>
          <div style={{ position: 'relative' }} ref={pdfMenuRef}>
            <button onClick={() => setShowPdfMenu(v => !v)} style={{ ...btnStyle, opacity: downloading ? 0.7 : 1 }} disabled={downloading}>
              {downloading ? '⏳ Downloading...' : '⬇ Download'}
            </button>
            {showPdfMenu && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, minWidth: '160px' }}>
                {[{ label: 'Last 1 Month', months: 1 }, { label: 'Last 3 Months', months: 3 }, { label: 'Last 6 Months', months: 6 }, { label: 'Last 1 Year', months: 12 }].map(opt => (
                  <button key={opt.months} onClick={() => downloadPdf(opt.months)}
                    style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#333', borderBottom: '1px solid #f0f0f0' }}
                    onMouseOver={e => e.currentTarget.style.background = '#f7f8fa'}
                    onMouseOut={e => e.currentTarget.style.background = 'none'}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary pills */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[['ALL', queries.length, '#232f3e'], ['OPEN', counts.OPEN, '#d97706'], ['IN_PROGRESS', counts.IN_PROGRESS, '#1d4ed8'], ['RESOLVED', counts.RESOLVED, '#16a34a'], ['CLOSED', counts.CLOSED, '#6b7280']].map(([s, c, col]) => (
          <button key={s} onClick={() => { setFilter(s); setExpanded(false); }}
            style={{ padding: '6px 16px', borderRadius: '20px', border: `1.5px solid ${filter === s ? col : '#e0e0e0'}`, background: filter === s ? col : 'white', color: filter === s ? 'white' : '#555', fontWeight: '600', fontSize: '12px', cursor: 'pointer' }}>
            {s === 'ALL' ? 'All' : s.replace('_', ' ')} ({c})
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
          <p style={{ margin: 0 }}>No queries found</p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f0f0f0', background: '#fafafa' }}>
                  {['#', 'Customer', 'Query Type', 'Invoice No.', 'Description', 'Image', 'Status', 'Date', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#888', fontWeight: '700', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SHOW.map((q, idx) => {
                  const sc = STATUS_COLORS[q.status] || STATUS_COLORS.OPEN;
                  const reverseId = filtered.length - idx;
                  return (
                    <tr key={q.id} style={{ borderBottom: '1px solid #f8f8f8' }}
                      onMouseOver={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseOut={e => e.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '12px', color: '#ccc', fontSize: '12px' }}>{reverseId}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: '700', color: '#1a1a1a' }}>{q.fullName}</div>
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{q.email}</div>
                        {q.phone && <div style={{ fontSize: '11px', color: '#aaa' }}>{q.phone}</div>}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ background: '#fff0f0', color: '#9A1111', borderRadius: '12px', padding: '3px 10px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {q.queryType}
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: '#555', fontSize: '12px' }}>{q.invoiceNo || '—'}</td>
                      <td style={{ padding: '12px', color: '#555', maxWidth: '220px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }} title={q.description}>
                          {q.description}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {q.imageUrl ? (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {q.imageUrl.split(',').map((url, i) => (
                              <a key={i} href={`${UPLOADS_URL}${url.trim()}`} target="_blank" rel="noreferrer">
                                <img src={`${UPLOADS_URL}${url.trim()}`} alt={`attachment-${i+1}`}
                                  style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e0e0e0', display: 'block' }} />
                              </a>
                            ))}
                          </div>
                        ) : <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <select value={q.status}
                          onChange={e => updateStatus(q.id, e.target.value)}
                          style={{ padding: '4px 10px', borderRadius: '12px', border: `1.5px solid ${sc.color}`, background: sc.bg, color: sc.color, fontWeight: '700', fontSize: '12px', cursor: 'pointer', outline: 'none' }}>
                          <option value="OPEN">Open</option>
                          <option value="IN_PROGRESS">In Progress</option>
                          <option value="RESOLVED">Resolved</option>
                          <option value="CLOSED">Closed</option>
                        </select>
                      </td>
                      <td style={{ padding: '12px', color: '#888', fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {q.createdAt ? new Date(q.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button onClick={() => deleteQuery(q.id)}
                          style={{ background: '#fff5f5', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '8px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filtered.length > 6 && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button onClick={() => setExpanded(e => !e)}
                style={{ background: 'none', border: '1.5px solid #e0e0e0', borderRadius: '8px', padding: '8px 24px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#555' }}>
                {expanded ? '▲ Show Less' : `▼ Show All ${filtered.length} Queries`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
